import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import type { IAuthSession } from '~/schemas/auth/authSchemas'
import type { IAddress } from '~/schemas/customer/address'
import type { ICustomer } from '~/schemas/customer/customer'

/** Sessão guardada (os endereços do cliente ficam em `customer.addresses`, lidos pelo `useAddresses`). */
type IAuthStoreData = IAuthSession

interface IAuthState {
    /** Sessão do cliente como a API devolve nos logins e no update-token (AuthSessionSchema). */
    data: IAuthStoreData | null
    setAuthData: (data: IAuthSession) => void
    deleteAuthCartData: () => void
    /**
     * Grava o cliente devolvido pela API (update, update-company-customer), mesclado com o que a store já tem: o update
     * não devolve tudo o que o login gravou. Sem sessão aberta, não faz nada. Chamada pelos services do domínio customer.
     */
    setCustomer: (customer: Partial<ICustomer>) => void
    /** Troca a lista de endereços do cliente (a lista completa que a API devolve). Sem sessão aberta, não faz nada. */
    setAddresses: (addresses: IAddress[]) => void
    resetStore: () => void
}

const initialState = {
    data: null as IAuthStoreData | null,
}

// Cache do cliente no localStorage (padrão do persist), com a chave de sempre: trocar a chave deslogaria todo mundo.
// Quem decide se há sessão é o cookie @glofi:token (proxy.ts e useSessionSync).
export const useAuthStore = create<IAuthState>()(
    devtools(
        persist(
            (set) => ({
                ...initialState,
                setAuthData: (data) => set({ data }, false, 'setAuthData'),
                deleteAuthCartData: () =>
                    set(
                        (state) => (state.data?.cart ? { data: { ...state.data, cart: null } } : state),
                        false,
                        'deleteAuthCartData',
                    ),
                setCustomer: (customer) =>
                    set(
                        (state) =>
                            state.data
                                ? { data: { ...state.data, customer: { ...state.data.customer, ...customer } } }
                                : state,
                        false,
                        'setCustomer',
                    ),
                setAddresses: (addresses) =>
                    set(
                        (state) =>
                            state.data
                                ? { data: { ...state.data, customer: { ...state.data.customer, addresses } } }
                                : state,
                        false,
                        'setAddresses',
                    ),
                resetStore: () => set(initialState, false, 'resetStore'),
            }),
            { name: 'auth-storage' },
        ),
        { name: 'auth-store' },
    ),
)

/** Cliente logado pelo cache da sessão (o cookie do token decide no proxy; aqui é o que a tela mostra). Seletor único. */
export const selectIsLoggedIn = (state: Pick<IAuthState, 'data'>): boolean => Boolean(state.data?.customer)
